import { useEffect, useState } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { Box, CircularProgress, Paper, Typography } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { OmFieldChange, OmHistory, OmVersion, lhRegistryApiRef } from './api';
import { keycloakAuthApiRef } from '../../apis';
import { Pill, fmtAt } from './ui';

const MONO = "'Roboto Mono', monospace";

const lines = (v: OmVersion): string[] => [
  ...v.fieldsAdded.map((c: OmFieldChange) => `+ ${c.name}`),
  ...v.fieldsUpdated.map(c => `~ ${c.name}${c.oldValue !== undefined || c.newValue !== undefined ? `: ${c.oldValue ?? '∅'} → ${c.newValue ?? '∅'}` : ''}`),
  ...v.fieldsDeleted.map(c => `− ${c.name}`),
];

/** OM 에 있는 이 테이블의 삭제 표시와 버전 이력 — 읽기 전용이며 레지스트리 근거 티켓과 무관하다 */
export const OmHistoryCard = ({ tableId }: { tableId: number }) => {
  const api = useApi(lhRegistryApiRef);
  const keycloak = useApi(keycloakAuthApiRef);
  const [h, setH] = useState<OmHistory>();
  const [error, setError] = useState<string>();

  useEffect(() => {
    let cancelled = false;
    // 토큰 발급 실패는 막지 않는다 — 권한 판단은 서버에 있다
    const token = async () => {
      try {
        return (await keycloak.getAccessToken()) || undefined;
      } catch {
        return undefined;
      }
    };
    token()
      .then(t => api.omHistory(tableId, t))
      .then(r => !cancelled && setH(r))
      .catch(e => !cancelled && setError(e.message));
    return () => {
      cancelled = true;
    };
  }, [api, keycloak, tableId]);

  const body = () => {
    if (error) return <Alert severity="warning">OM 이력을 불러오지 못했습니다: {error}</Alert>;
    if (!h) return <CircularProgress size={20} />;
    if (h.state === 'unsupported_source') return <Typography variant="body2" color="textSecondary">RDB 외 원천은 OM 이력 미지원 (DPD-14025)</Typography>;
    if (h.state === 'truncated') return <Typography variant="body2" color="textSecondary">OM({h.serviceName}) 테이블 수가 많아 확인하지 못했습니다.</Typography>;
    if (h.state === 'ambiguous')
      return (
        <>
          <Alert severity="warning">여러 OM 테이블과 일치 — 확인 불가</Alert>
          {h.candidates.map(c => <Typography key={c} variant="caption" component="div" style={{ fontFamily: MONO, wordBreak: 'break-all' }}>{c}</Typography>)}
        </>
      );
    if (h.state === 'no_service') return <Typography variant="body2" color="textSecondary">이 원천의 OM 서비스가 없습니다.</Typography>;
    if (h.state === 'not_found') return <Typography variant="body2" color="textSecondary">OM({h.serviceName})에 이 테이블이 없습니다 — 아직 ingest 전일 수 있습니다.</Typography>;
    return (
      <>
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          <Typography variant="caption" color="textSecondary">서비스 {h.serviceName}</Typography>
          {h.deleted && <Pill tone="error">OM 에서 삭제됨</Pill>}
        </Box>
        {!h.versions.length && <Typography variant="body2" color="textSecondary">버전 이력이 없습니다.</Typography>}
        {h.versions.map(v => (
          <Box key={v.version} style={{ borderLeft: '2px solid rgba(128,128,128,.4)', paddingLeft: 10 }}>
            <Typography variant="body2">
              <b>v{v.version}</b>{v.updatedAt ? ` · ${fmtAt(new Date(v.updatedAt).toISOString())}` : ''}{v.updatedBy ? ` · ${v.updatedBy}` : ''}
              {v.deleted && <> <Pill tone="error">삭제 표시</Pill></>}
            </Typography>
            {lines(v).map((l, i) => <Typography key={i} variant="caption" component="div" style={{ fontFamily: MONO, wordBreak: 'break-all' }}>{l}</Typography>)}
          </Box>
        ))}
      </>
    );
  };

  return (
    <Paper variant="outlined" component="section" aria-label="OM 이력">
      <Box p={2.5} display="flex" flexDirection="column" style={{ gap: 10 }}>
        <Typography variant="subtitle1">OM 이력</Typography>
        {body()}
      </Box>
    </Paper>
  );
};
